import { rcTreeAdapter } from '@consta/rc-tree-adapter/rcTreeAdapter'
import { findTreeNode } from '@core/core/helpers'
import {
  getNodesByParentId,
  getTopNodesByProjectId,
  selectActiveEntity,
  selectActiveProjectId,
  selectActiveTab,
  selectActiveTree,
  setActiveEntity,
  setTabElementState,
  useAppDispatch,
  useAppSelector,
} from '@core/core/store'
import type { SelectedTreeObject } from '@core/core/types'
import type { TabElementStateType } from 'common/types'
import { default as RCTree } from 'rc-tree'
import type { DataNode, EventDataNode, Key } from 'rc-tree/lib/interface'
import type { FC, MouseEvent as ReactMouseEvent } from 'react'
import { useEffect, useRef } from 'react'

import { getTreeNode, isObjectTreeNodeProps } from './getTreeNode'
import css from './Tree.css'
import type { TreePropsType as PropsType } from './types'

const treeProps = rcTreeAdapter({ size: 'm' })

export const Tree: FC<PropsType> = ({ changeMenuVisibility }) => {
  const dispatch = useAppDispatch()
  const activeTree = useAppSelector(selectActiveTree)
  const activeTab = useAppSelector(selectActiveTab)
  const activeProjectId = useAppSelector(selectActiveProjectId)
  const activeEntity = useAppSelector(selectActiveEntity)
  const activeEntityTreeId = useRef<string>('')

  if (activeEntity) {
    const found = findTreeNode(activeTree, activeEntity)
    if (found?.uid) {
      activeEntityTreeId.current = found.uid
    }
  }

  useEffect(() => {
    dispatch(setActiveEntity(null))
  }, [])

  useEffect(() => {
    if (activeProjectId !== '') {
      dispatch(getTopNodesByProjectId(activeProjectId))
    }
  }, [activeProjectId])

  const onExpand = (
    _: Key[],
    expanded: {
      node: EventDataNode<DataNode>
      expanded: boolean
      nativeEvent: MouseEvent
    },
  ) => {
    const item = expanded.node.key
    if (item) {
      dispatch(getNodesByParentId(activeProjectId, item.toString(), activeTab?.id))
    }
  }

  const onChangeStateChecked =
    (uid: string) =>
    ({ checked, indeterminateState }: { checked: boolean; indeterminateState: boolean }) => {
      let state: TabElementStateType
      if (checked && !indeterminateState) state = 'Checked'
      else state = 'Unchecked'
      if (!activeTab) return

      dispatch(setTabElementState(activeProjectId, uid, activeTab.id, state))
    }

  const onNodeSelect = (e: ReactMouseEvent, eventNode: EventDataNode<DataNode>) => {
    isObjectTreeNodeProps(eventNode.title)

    const treeNode = eventNode.title.props.objectTreeNode

    const getNodePropertyValueByType = (type: string) =>
      treeNode.properties?.find(({ propertyType }) => propertyType === type)?.value

    const entity: SelectedTreeObject = {
      entityName: treeNode.name,
      entityType: treeNode.elementType,
      entityId: treeNode.projectEntityUid,
      customProperties: {
        strokeColor: getNodePropertyValueByType('StrokeColor'),
        fillColor: getNodePropertyValueByType('FillColor'),
        fontSize: getNodePropertyValueByType('FontSize'),
      },
    }

    dispatch(setActiveEntity(entity))
    activeEntityTreeId.current = eventNode.key.toString()
  }

  const treeData = activeTree.map(node =>
    getTreeNode(activeTab, node, changeMenuVisibility, onChangeStateChecked),
  )

  return (
    <div className={css.container}>
      <RCTree
        {...treeProps}
        treeData={treeData}
        selectedKeys={[activeEntityTreeId.current]}
        onExpand={onExpand}
        onClick={onNodeSelect}
      />
    </div>
  )
}
